import { z } from 'zod';

/**
 * W25 — data-layer zero trust: Postgres row-level security status, the tamper-evident audit
 * chain, Cloudflare Turnstile on public web forms, and the public message box (grievance +
 * contact) on the site. See docs/W25-DESIGN.md.
 */

/* ------------------------------------------------------------------ RLS */

/** rls.mode (env DB_RLS_MODE). Only meaningful with FEATURE_DB_RLS=true. */
export const RLS_MODES = ['off', 'shadow', 'enforce'] as const;
export const RlsModeSchema = z.enum(RLS_MODES);
export type RlsMode = z.infer<typeof RlsModeSchema>;

/** The app.realm values the database policies understand. */
export const DB_REALMS = [
  'customer',
  'partner',
  'rider',
  'mi',
  'staff',
  'support',
  'system',
  'public',
] as const;
export type DbRealm = (typeof DB_REALMS)[number];

/** What a policy keys a table's rows on (shown on the RLS status page). */
export const RLS_OWNERSHIP = [
  'customer',
  'order',
  'partner_outlet',
  'rider',
  'mi',
  'support_ticket',
  'device',
  'mixed',
] as const;
export type RlsOwnership = (typeof RLS_OWNERSHIP)[number];

export interface RlsTableStatus {
  table: string;
  /** Domain group for the page ("Customers", "Orders", "Money", …). */
  group: string;
  ownership: RlsOwnership;
  /** Present in the database (false when the migration has not run). */
  exists: boolean;
  rlsEnabled: boolean;
  rlsForced: boolean;
  policies: number;
}

export interface RlsShadowDiff {
  fingerprint: string;
  sqlHead: string;
  realm: string;
  route: string | null;
  count: number;
  lastAt: string;
  ownerRows: number;
  appRows: number | null;
  errorCode: string | null;
}

export interface RlsShadowRunSummary {
  at: string;
  mode: 'shadow' | 'enforce';
  checks: number;
  diffs: number;
}

export interface RlsStatus {
  flag: boolean;
  mode: RlsMode;
  /** The login the API process uses (user name only, never the password). */
  login: string;
  loginKind: 'owner' | 'app' | 'worker' | 'readonly' | 'other';
  /** migrate:0026 applied. */
  migrated: boolean;
  /** Roles present in the database (capsico_owner / app / worker / readonly). */
  roles: { name: string; exists: boolean; canLogin: boolean; bypassRls: boolean }[];
  tables: RlsTableStatus[];
  shadow: {
    windowHours: number;
    sampleRate: number;
    mirrored: number;
    diffs: number;
    errors: number;
    topDiffs: RlsShadowDiff[];
    lastRun: RlsShadowRunSummary | null;
  };
}

/* ------------------------------------------------------------------ audit chain */

export const AUDIT_CHECK_STATUSES = ['ok', 'broken', 'empty'] as const;
export type AuditCheckStatus = (typeof AUDIT_CHECK_STATUSES)[number];

export const AUDIT_BREAK_REASONS = [
  'hash_mismatch',
  'link_mismatch',
  'gap',
  'unchained_rows',
  'head_mismatch',
  'trigger_disabled',
  'anchor_mismatch',
] as const;
export type AuditBreakReason = (typeof AUDIT_BREAK_REASONS)[number];

export interface AuditChainCheck {
  at: string;
  status: AuditCheckStatus;
  rowsChecked: number;
  headSeq: number;
  brokenAtSeq: number | null;
  reason: AuditBreakReason | null;
  anchored: boolean;
  actor: string;
}

export interface AuditChainStatus {
  chain: string;
  label: string;
  /** Rows on the chain (head sequence). */
  headSeq: number;
  headHash: string | null;
  headAt: string | null;
  triggersOk: boolean;
  lastCheck: AuditChainCheck | null;
}

export interface AuditIntegrity {
  /** FEATURE_AUDIT_CHAIN (nightly verify + alerts). The triggers run whenever 0026 is applied. */
  enabled: boolean;
  migrated: boolean;
  anchorConfigured: boolean;
  lastVerifiedAt: string | null;
  brokenChains: number;
  chains: AuditChainStatus[];
}

export const AuditVerifyInputSchema = z
  .object({
    /** One chain, or every chain when omitted. */
    chain: z
      .string()
      .regex(/^[a-z][a-z0-9_]{2,62}$/)
      .optional(),
  })
  .strict();
export type AuditVerifyInput = z.infer<typeof AuditVerifyInputSchema>;

export const AuditChainParamsSchema = z
  .object({ chain: z.string().regex(/^[a-z][a-z0-9_]{2,62}$/) })
  .strict();

export const AuditExportQuerySchema = z
  .object({
    afterSeq: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(5000).default(1000),
  })
  .strict();
export type AuditExportQuery = z.infer<typeof AuditExportQuerySchema>;

/** One link of a chain as exported for an auditor (hashes only, never the row payload). */
export interface AuditExportRow {
  chainSeq: number;
  prevHash: string;
  rowHash: string;
  /** The hash recomputed from the stored row right now. */
  expected: string;
  ok: boolean;
}

export interface AuditExportPage {
  chain: string;
  rows: AuditExportRow[];
  nextAfterSeq: number | null;
}

/* ------------------------------------------------------------------ Turnstile */

/** Header carrying the Cloudflare Turnstile token on protected public web forms. */
export const TURNSTILE_HEADER = 'x-turnstile-token';
export const TURNSTILE_ERROR_CODES = {
  REQUIRED: 'turnstile_required',
  FAILED: 'turnstile_failed',
} as const;
export const TURNSTILE_MESSAGES = {
  required: 'Please complete the check above and try again.',
  failed: 'We could not confirm you are a person. Please try the check again.',
} as const;

/* ------------------------------------------------------------------ public messages (site) */

export const PUBLIC_MESSAGE_KINDS = ['grievance', 'contact'] as const;
export const PublicMessageKindSchema = z.enum(PUBLIC_MESSAGE_KINDS);
export type PublicMessageKind = z.infer<typeof PublicMessageKindSchema>;

export const PublicMessageInputSchema = z
  .object({
    kind: PublicMessageKindSchema,
    country: z.enum(['IN', 'NP']),
    name: z.string().trim().min(2).max(80),
    /** Phone or e-mail, so the officer can reply. */
    contact: z.string().trim().min(5).max(120),
    /** Optional order reference or CAP- support code. */
    reference: z.string().trim().max(40).optional(),
    message: z.string().trim().min(10).max(2000),
  })
  .strict();
export type PublicMessageInput = z.infer<typeof PublicMessageInputSchema>;

export interface PublicMessageReceipt {
  /** Short reference shown to the sender ("G-7K2Q9M"). */
  reference: string;
  receivedAt: string;
}
